import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Minus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, getApiPath } from "@/lib/queryClient";
import { refreshProcurementPage } from "@/lib/procurementRefresh";

async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries: number = 3
): Promise<Response> {
  let lastError: Error | null = null;
  const scopedUrl = getApiPath(url);
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch(scopedUrl, options);
      return response;
    } catch (error: any) {
      lastError = error;
      const isNetworkError = error.message === "Load failed" || 
                             error.message === "Failed to fetch" ||
                             error.name === "TypeError";
      
      if (!isNetworkError || attempt === maxRetries - 1) {
        throw error;
      }
      
      const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError || new Error("Request failed after retries");
}

interface ProcurementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: number;
  itemId: number;
  productId: number;
  productName: string;
  currentProcured: number;
  totalOrdered: number;
  selectedPeriod?: string;
  dateRange?: { start: string | null; end: string | null };
  isFullyProcured?: boolean;
}

export function ProcurementDialog({
  isOpen,
  onClose,
  orderId,
  itemId,
  productId,
  productName,
  currentProcured,
  totalOrdered,
  selectedPeriod = "all",
  dateRange = { start: null, end: null },
  isFullyProcured: initialIsFullyProcured = false
}: ProcurementDialogProps) {
  const [procuredQuantity, setProcuredQuantity] = useState(currentProcured);
  const [editingQuantity, setEditingQuantity] = useState(false);
  const [editQuantityValue, setEditQuantityValue] = useState("");
  const [isFullyProcured, setIsFullyProcured] = useState(initialIsFullyProcured);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Reset the quantity when dialog opens with new data
  useEffect(() => {
    // Default to max quantity (totalOrdered) to make updating effortless
    setProcuredQuantity(totalOrdered);
    // Reset editing state
    setEditingQuantity(false);
    setEditQuantityValue("");
    // Reset isFullyProcured to initial value
    setIsFullyProcured(initialIsFullyProcured);
  }, [currentProcured, totalOrdered, itemId, isOpen, initialIsFullyProcured]);

  const updateProcurementMutation = useMutation({
    mutationFn: async (procuredQuantity: number) => {
      // Use the product-level API for total procurement management with retry logic
      const response = await fetchWithRetry(`/api/products/${productId}/procured`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          totalProcuredQuantity: procuredQuantity,
          period: selectedPeriod,
          dateRange: dateRange,
          isFullyProcured: isFullyProcured
        })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Server error - please try again");
      }
      return response.json();
    },
    onSuccess: () => {
      refreshProcurementPage(queryClient);
      toast({
        title: "Success",
        description: "Procurement quantity updated successfully",
      });
      onClose();
    },
    onError: (error: any) => {
      const isNetworkError = error.message === "Load failed" || 
                             error.message === "Failed to fetch";
      toast({
        title: "Error",
        description: isNetworkError 
          ? "Network error - please check your connection and try again" 
          : (error.message || "Failed to update procurement quantity"),
        variant: "destructive",
      });
    }
  });

  const resetProcurementMutation = useMutation({
    mutationFn: async () => {
      const response = await fetchWithRetry(`/api/products/${productId}/procured/reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          period: selectedPeriod,
          dateRange: dateRange
        })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Server error - please try again");
      }
      return response.json();
    },
    onSuccess: () => {
      refreshProcurementPage(queryClient);
      toast({
        title: "Success",
        description: "Item reset to pending status",
      });
      onClose();
    },
    onError: (error: any) => {
      const isNetworkError = error.message === "Load failed" || 
                             error.message === "Failed to fetch";
      toast({
        title: "Error", 
        description: isNetworkError 
          ? "Network error - please check your connection and try again" 
          : (error.message || "Failed to reset procurement"),
        variant: "destructive",
      });
    }
  });

  const handleIncrement = () => {
    if (procuredQuantity < totalOrdered) {
      setProcuredQuantity(prev => prev + 1);
    }
  };

  const handleDecrement = () => {
    if (procuredQuantity > 0) {
      setProcuredQuantity(prev => prev - 1);
    }
  };

  const handleQuantityClick = (currentQuantity: number) => {
    setEditQuantityValue(currentQuantity.toString());
    setEditingQuantity(true);
  };

  const handleQuantityInputSave = () => {
    const newQuantity = parseInt(editQuantityValue) || 0;
    const clampedQuantity = Math.max(0, Math.min(newQuantity, totalOrdered));
    setProcuredQuantity(clampedQuantity);
    setEditingQuantity(false);
    setEditQuantityValue("");
  };

  const handleQuantityInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleQuantityInputSave();
    } else if (e.key === 'Escape') {
      setEditingQuantity(false);
      setEditQuantityValue("");
    }
  };

  const handleUpdate = () => {
    // Prevent setting procurement on items with 0 ordered quantity
    if (totalOrdered === 0) {
      toast({
        title: "Cannot Procure",
        description: "Cannot set procurement for items with 0 quantity ordered",
        variant: "destructive",
      });
      return;
    }
    updateProcurementMutation.mutate(procuredQuantity);
  };

  const handleResetClick = () => {
    setIsResetDialogOpen(true);
  };

  const handleResetConfirm = () => {
    setIsResetDialogOpen(false);
    resetProcurementMutation.mutate(undefined);
  };

  const handleClose = () => {
    setProcuredQuantity(currentProcured); // Reset to original value
    setEditingQuantity(false);
    setEditQuantityValue("");
    onClose();
  };

  // Enhanced close handler to prevent event propagation
  const handleDialogChange = (open: boolean) => {
    if (!open) {
      // Reset to original value and prevent event propagation when closing dialog
      setProcuredQuantity(currentProcured);
      setEditingQuantity(false);
      setEditQuantityValue("");
      setTimeout(() => onClose(), 0);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogContent 
        className="!w-[90vw] !max-w-md sm:!max-w-lg mx-auto p-6 sm:p-8" 
        data-testid="procurement-dialog"
        onPointerDownOutside={(e) => {
          e.stopPropagation();
        }}
        onInteractOutside={(e) => {
          e.stopPropagation();
        }}
      >
        <DialogHeader>
          <DialogTitle data-testid="dialog-title">Update Procurement</DialogTitle>
          <p className="text-sm text-gray-600" data-testid="product-name">
            {productName}
          </p>
        </DialogHeader>
        
        <div className="flex flex-col items-center space-y-8 py-8">
          {/* Quantity Controls and Mark Procured Button */}
          <div className="mx-auto w-fit">
            <div className="flex items-center space-x-6">
              <Button
                onClick={handleDecrement}
                disabled={procuredQuantity <= 0}
                className="bg-[#1D3D88] hover:bg-[#1D3D88]/90 text-white rounded-lg p-4 min-w-[56px] h-14"
                data-testid="button-decrement"
              >
                <Minus className="h-5 w-5" />
              </Button>
              
              <div className="text-center min-w-[140px]">
                {editingQuantity ? (
                  <Input
                    type="number"
                    value={editQuantityValue}
                    onChange={(e) => setEditQuantityValue(e.target.value)}
                    onBlur={handleQuantityInputSave}
                    onKeyDown={handleQuantityInputKeyDown}
                    className="text-3xl font-bold text-center h-14 p-3 w-full [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                    autoFocus
                    data-testid="input-procured-quantity"
                  />
                ) : (
                  <div 
                    className="text-3xl font-bold text-google-blue cursor-pointer hover:bg-gray-100 rounded p-3 transition-colors" 
                    onClick={() => handleQuantityClick(procuredQuantity)}
                    data-testid="text-procured-quantity"
                  >
                    {procuredQuantity}
                  </div>
                )}
                <div className="text-base text-gray-500 mt-2" data-testid="text-total-ordered">
                  / {totalOrdered} units procured
                </div>
              </div>
              
              <Button
                onClick={handleIncrement}
                disabled={procuredQuantity >= totalOrdered}
                className="bg-[#1D3D88] hover:bg-[#1D3D88]/90 text-white rounded-lg p-4 min-w-[56px] h-14"
                data-testid="button-increment"
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>
            
            {/* Mark/Unmark Procured Button - only show when fully procured in quantity */}
            {procuredQuantity >= totalOrdered && totalOrdered > 0 && (
              <Button
                variant={!isFullyProcured ? "default" : "outline"}
                onClick={() => setIsFullyProcured(!isFullyProcured)}
                className={`mt-8 h-12 w-full text-base font-medium ${
                  !isFullyProcured 
                    ? "bg-cn-brand-green hover:bg-cn-brand-green-hover text-white" 
                    : "border-blue-600 text-blue-600 hover:bg-blue-50"
                }`}
                data-testid="button-toggle-procured"
              >
                {isFullyProcured ? "Unmark Procured" : "Mark Procured"}
              </Button>
            )}
          </div>
        </div>


        <DialogFooter className="flex flex-col gap-3 pt-6">
          {/* Action buttons */}
          <div className="flex gap-2 w-full max-w-xs mx-auto justify-center sm:w-auto sm:max-w-none sm:mx-0 sm:justify-end">
            <Button
              variant="outline"
              onClick={handleResetClick}
              disabled={updateProcurementMutation.isPending || resetProcurementMutation.isPending}
              className="text-red-600 border-red-300 hover:bg-red-50 flex-1 sm:min-w-[80px] sm:flex-none"
              data-testid="button-reset"
            >
              {resetProcurementMutation.isPending ? "Resetting..." : "Reset"}
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={updateProcurementMutation.isPending || resetProcurementMutation.isPending}
              className="bg-cn-brand-blue hover:bg-cn-brand-blue-hover text-white flex-[2] sm:min-w-[160px] sm:flex-none"
              data-testid="button-update"
            >
              {updateProcurementMutation.isPending ? "Updating..." : "Update"}
            </Button>
          </div>
        </DialogFooter>
        
        {/* Reset Confirmation Dialog */}
        <AlertDialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset to pending?</AlertDialogTitle>
              <AlertDialogDescription>
                Current quantities will be set to 0
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setIsResetDialogOpen(false)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleResetConfirm}
                className="bg-red-600 hover:bg-red-700"
              >
                Reset
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}